import { FirebaseApp, FirebaseError, initializeApp } from 'firebase/app';
import { Auth, getAuth, User, signInWithEmailAndPassword } from 'firebase/auth';
import { Firestore, getFirestore } from "firebase/firestore";
import { FirebaseStorage, getStorage } from 'firebase/storage';


import { createContext, FC, useCallback, useContext, useEffect, useState } from 'react';

const firebaseConfig = {
    apiKey: "AIzaSyBoN_WZZo3iSA1auo1MBFNSvUc3L1Qp438",
    authDomain: "ecgnotes-ab029.firebaseapp.com",
    projectId: "ecgnotes-ab029",
    storageBucket: "ecgnotes-ab029.appspot.com",
    messagingSenderId: "39334708111",
    appId: "1:39334708111:web:ff567f978b5c7b893201fa"
};
  
// MARK: React Context

export type FirebaseContextType = {
    /**
     * The Firebase app.
     */
    app: FirebaseApp;

    /**
     * The Firebase authentication service.
     */
    auth: Auth;

    /**
     * The current user.
     */
    user: User | null;

    /**
     * Sets the current user.
     */
    setUser: (user: User | null) => void;

    /**
     * Signs in with the given email and password.
     * @param email - The email address.
     * @param password - The password.
     */
    signIn: (email: string, password: string) => void;

    /**
     * Signs in with the given email and password.
     * @param email - The email address.
     * @param password - The password.
     */
    signInAsync: (email: string, password: string) => Promise<{ failed: boolean, user?: User, error?: FirebaseError }>;

    /**
     * Signs out the current user.
     */
    signOut: () => void;

    /**
     * Returns true if a user is signed in, false otherwise.
     * @returns true if a user is signed in, false otherwise.
     */
    isUserSignedIn: () => boolean;

    /**
     * The current error message.
     */
    error: string | null;

    /**
     * The Firestore database.
     */
    db: Firestore;

    /**
     * The Firebase storage service.
     */
    storage: FirebaseStorage;
};

export const FirebaseContext = createContext<FirebaseContextType | undefined>(undefined);

export const FirebaseProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
    const app = initializeApp(firebaseConfig);
    const auth = getAuth(app);
    const db = getFirestore(app);
    const storage = getStorage(app);

    const [user, setUser] = useState<User | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        auth.onAuthStateChanged(newUser => {
            setUser(newUser);
        });
    }, [auth]);

    const signIn = useCallback((email: string, password: string) => {
        signInWithEmailAndPassword(auth, email, password)
            .then(userCredential => {
                console.log(`Signed in as ${userCredential.user?.email}`);
            })
            .catch(error => {
                console.log(`Failed to sign in: ${error.message}`);

                setError(error.message);
            });
    }, [auth]);

    const signInAsync = useCallback(async (email: string, password: string) => {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);

            console.log(`Signed in as ${userCredential.user?.email}`);

            return { failed: false, user: userCredential.user };
        } catch (error) {
            const err = (error as FirebaseError);

            setError(err.message);

            console.error(`Failed to sign in: ${err.message}`);

            return { failed: true, error: err };
        }
    }, [auth]);

    const signOut = useCallback(() => {
        auth.signOut()
            .then(() => {
                console.log('Signed out');
            })
            .catch(error => {
                console.log(`Failed to sign out: ${error.message}`);

                setError(error.message);
            });
    }, [auth]);

    const isUserSignedIn = useCallback(() => {
        return user !== null;
    }, [user]);

    const contextValue = { app, auth, user, setUser, signIn, signInAsync, signOut, error, isUserSignedIn, db, storage };

    return (
        <FirebaseContext.Provider value={contextValue}>
            {children}
        </FirebaseContext.Provider>
    );
};

export const useFirebase = (): FirebaseContextType => {
    const context = useContext(FirebaseContext);

    if (context === undefined) {
        throw new Error('useFirebase must be used within a FirebaseProvider');
    }

    return context;
};